<template>
  <v-form ref="phoneNumberForm" @submit.prevent="handleSubmit">
    <div v-show="shouldShowInputs">
      <div class="phone-number-inputs">
        <va-select-input
          :source="'campaignName'"
          v-model="campaignFilter"
          :label="$t('resources.pages.campaign')"
          :placeholder="$t('resources.pages.campaign_placeholder')"
          :choices="uniqueCampaigns"
          item-text="campaignName"
          required
          @change="onCampaignChange">
        </va-select-input>

        <va-select-input
          class="spacing-bottom--16 spacing-top--16"
          :source="'publisherName'"
          v-model="selectedPublisher"
          :label="$t('resources.pages.publisher')"
          :placeholder="$t('resources.pages.publisher_placeholder')"
          :choices="uniquePublishers"
          item-text="publisherName"
          item-value="@id"
          required
          @change="onPublisherChange">
        </va-select-input>
      </div>
    </div>
    <slot :onCancel="handleCancel" :isValid="isFormValid" :formData="formData"> </slot>
  </v-form>
</template>

<script>
  export default {
    name: "RingbaPhoneNumber",
    props: {
      value: {
        type: Object,
        default: () => ({}),
      },
      phoneNumbers: {
        type: Array,
        required: true,
        default: () => [],
      },
      currentVariables: {
        type: Array,
        required: false,
        default: () => [],
      },
      isEdit: {
        type: Boolean,
        required: false,
        default: false,
      },
    },
    data() {
      return {
        selectedPublisher: null,
        campaignFilter: null,
        isFormValid: false,
        showValidation: false,
      };
    },
    mounted() {
      if (this.value?.phoneNumber) {
        const phone = this.phoneNumbers.find(p => p["@id"] === this.value.phoneNumber);
        this.campaignFilter = phone?.campaignName;
        this.selectedPublisher = phone["@id"];
      } else if (this.value?.campaignName) {
        this.campaignFilter = this.value.campaignName;
        this.selectedPublisher = this.value["@id"];
      }
    },
    watch: {
      value: {
        handler(newVal, oldVal) {
          if (
            newVal?.publisherName !== this.selectedPublisher?.publisherName &&
            (!oldVal || newVal?.phoneNumber !== oldVal?.phoneNumber)
          ) {
            this.showValidation = false;

            if (newVal) {
              this.campaignFilter = newVal.campaignName || this.campaignFilter;
              this.selectedPublisher = newVal["@id"] || this.selectedPublisher;
            }

            this.$nextTick(() => {
              this.showValidation = Boolean(this.selectedPublisher || this.campaignFilter);
            });
          }
        },
        immediate: true,
      },
      $data: {
        handler() {
          if (this.selectedPublisher !== this.value?.["@id"]) {
            this.emitUpdate();
          }
        },
        deep: true,
      },
    },
    computed: {
      filteredPhoneNumbers() {
        const usedPhoneNumbers = this.currentVariables.map(v => v.phoneNumberData["@id"]);

        if (this.value?.phoneNumberData) {
          return this.phoneNumbers.filter(
            phone => phone["@id"] === this.value.phoneNumberData["@id"] || !usedPhoneNumbers.includes(phone["@id"]),
          );
        }

        return this.phoneNumbers.filter(phone => !usedPhoneNumbers.includes(phone["@id"]));
      },
      uniqueCampaigns() {
        return this.filteredPhoneNumbers.filter(
          (campaign, index, self) => index === self.findIndex(c => c.campaignName === campaign.campaignName),
        );
      },
      uniquePublishers() {
        if (!this.campaignFilter) return [];

        return this.filteredPhoneNumbers.filter(phone => phone.campaignName === this.campaignFilter);
      },
      formData() {
        if (!this.selectedPublisher) return null;

        const phoneNumberData = this.phoneNumbers.find(p => p["@id"] === this.selectedPublisher);
        if (!phoneNumberData) return null;

        return {
          ...this.value,
          phoneNumberData,
          type: "phone_number",
          campaignName: this.campaignFilter,
          publisherName: phoneNumberData.publisherName,
          text: phoneNumberData.campaignName + " / " + phoneNumberData.publisherName,
        };
      },
      shouldShowInputs() {
        const usedPhoneNumbers = this.currentVariables.map(v => v.phoneNumberData["@id"]);
        const unusedNumbers = this.phoneNumbers.filter(phone => !usedPhoneNumbers.includes(phone["@id"]));

        return unusedNumbers.length > 0;
      },
    },
    methods: {
      async onPublisherChange(data) {
        this.isFormValid = this.isEdit && (await this.isValid()) && data === this.value?.phoneNumber ? false : true;
      },
      async isValid() {
        await this.$nextTick();

        return this.$refs.phoneNumberForm?.validate() || false;
      },
      async onCampaignChange(campaign) {
        if (!campaign) {
          this.selectedPublisher = null;
        } else {
          const currentPublisher = this.uniquePublishers.find(p => p["@id"] === this.selectedPublisher);
          if (!currentPublisher) {
            this.selectedPublisher = null;
          }
        }

        if (this.selectedPublisher) {
          this.showValidation = true;
        }
      },
      emitUpdate() {
        if (!this.selectedPublisher) {
          this.$emit("input", null);

          return;
        }

        const publisherData = this.phoneNumbers.find(p => p["@id"] === this.selectedPublisher);

        this.$emit("input", {
          ...this.value,
          ...publisherData,
          type: "phone_number",
          campaignName: this.campaignFilter,
        });
      },
      async handleSubmit(e) {
        if (e?.type === "submit") {
          e.preventDefault();
        }
        this.showValidation = true;
        await this.$nextTick();
        const isValid = await this.isValid();
        this.isFormValid = isValid;

        if (isValid && this.selectedPublisher && this.campaignFilter) {
          this.showValidation = false;
          this.$emit("submit", this.formData);
          await this.$nextTick();
          this.showValidation = true;
        }
      },
      handleCancel() {
        this.$emit("cancel");
      },
    },
  };
</script>

<style scoped>
  .label-select-item {
    padding: 8px 16px;
  }
</style>
